import { useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-jalaali';
import { parseFloatNull } from '../../../../../helper';

/**
 * Custom Hook برای مدیریت سفارش‌ها
 * @param {Object} config - تنظیمات مورد نیاز
 * @param {string} config.server_url - آدرس سرور
 * @param {Object} config.user - اطلاعات کاربر
 * @param {Object} config.dates - آرایه تاریخ‌ها
 * @returns {Object} { orders, loading, getOrders, refreshOrders, error, pagination, statusCounts, currentStatus, total, lastPage, getOrdersByStatusFromServer, getAllOrders, goToNextPage, goToPrevPage, goToPage }
 */
const useOrders = ({ server_url, user, dates, image_url }) => {
  const [orders, setOrders] = useState([]);
  const [defaultOrders, setDefaultOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null); // فیلتر فعلی
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [statusCounts, setStatusCounts] = useState({});

  // تابع پردازش اطلاعات هر سفارش
  const processOrderData = useCallback(
    (order, index) => {
      const orderState = {};
      setDefaultOrders(orders);
      // === اطلاعات پایه ===
      orderState.orders_id = order.id;
      orderState.order_id = order.id;
      orderState.type = order.type;
      orderState.document_id = order.document_id;
      orderState.status = order.status;
      orderState.gold_price = order.gold_price;
      orderState.date_purchased = order.date_purchased;
      orderState.time = order.time;
      orderState.fa_date_purchased = order.fa_date_purchased;
      orderState.ago = order.ago;
      orderState.payment_method = order.payment_method;
      orderState.payment_method_name = order.payment_method_name;
      orderState.order_price = order.price;
      orderState.price = order.price;

      // === تاریخ و زمان ===
      orderState.date = order.fa_date_purchased;
      orderState.register_date = moment(order.date_purchased).format('jYYYY/jM/jD HH:mm');

      // === شماره سند ===
      orderState.shomare_sanad = order.document_id || (order.status === 'failed' ? 'ناموفق' : 'درحال انجام');

      // === کد پیگیری ===
      orderState.tracking_code = order.document_id || `ORD-${order.id}`;

      // === قیمت طلا فرمت شده ===
      const goldPriceNumber = typeof order.gold_price === 'string' ? parseFloat(order.gold_price.replace(/,/g, '')) : parseFloat(order.gold_price || 0);
      orderState.gold_price_formatted = goldPriceNumber.toLocaleString('fa-IR');

      // === اطلاعات ارسال ===
      orderState.shipping_cost = parseFloat(order.shipping?.shipping_cost || 0);
      orderState.shipping_cost_formatted = orderState.shipping_cost.toLocaleString('fa-IR');
      orderState.shipping_method = order.shipping?.shipping_method;
      orderState.shipping_name = order.shipping?.shipping_name;
      orderState.shipping_duration = order.shipping?.shipping_duration;

      // === مبلغ کل ===
      const totalAmount = typeof order.price === 'string' ? parseFloat(order.price.replace(/,/g, '')) : parseFloat(order.price || 0);
      orderState.total_amount = totalAmount.toLocaleString('fa-IR');
      orderState.total_amount_raw = totalAmount;

      // === نوع پرداخت ===
      orderState.payment_type_text = order.payment_method_name;

      // === پردازش محصولات ===
      orderState.products_info = [];
      orderState.all_product_images = [];
      if (order?.payment_link) {
        orderState.payment_link = order.payment_link;
      }

      if (order?.payment_deposit_final) {
        orderState.payment_deposit_final = order.payment_deposit_final;
      }

      if (order.products && order.products.length > 0) {
        order.products.forEach((productItem) => {
          const product = productItem.product;

          // استخراج تصاویر محصول
          const productImages = [];
          if (product?.album && product?.album.images && product?.album.images.length > 0) {
            // فقط اولین عکس رو میگیریم
            const firstImage = product.album.images[0];
            const imageUrl = firstImage.url.startsWith('http') ? firstImage.url : image_url + firstImage.url;
            productImages.push(imageUrl);
            orderState.all_product_images.push(imageUrl);
          }

          // اطلاعات محصول
          const productInfo = {
            name: product?.name || 'محصول طلا',
            barcode: product?.barcode,
            weight: parseFloat(product?.weight || 0),
            price: parseFloat(productItem?.final_price?.replace(/,/g, '') || 0),
            final_price: parseFloat(productItem?.final_price?.replace(/,/g, '') || 0),
            images: productImages,
            image: productImages[0] || null,
            videos: product?.album?.videos || [],
            album_id: product?.album?.id,
          };

          orderState.products_info.push(productInfo);
        });
      }

      // === اولین تصویر محصول ===
      orderState.first_product_image = orderState.all_product_images[0] || null;

      // === تعداد کل تصاویر ===
      orderState.total_images_count = order.products.length;

      // === مجموع وزن ===
      orderState.total_weight = orderState.products_info.reduce((sum, product) => {
        return sum + parseFloat(product.weight || 0);
      }, 0);

      // === قیمت کل محصولات ===
      const totalProductPrice = orderState.products_info.reduce((sum, product) => {
        return sum + parseFloat(product.final_price || 0);
      }, 0);

      const totalOrderPrice = totalProductPrice + orderState.shipping_cost;
      orderState.total_price = totalOrderPrice;
      orderState.total_price_formatted = totalOrderPrice.toLocaleString('fa-IR');

      // === محاسبات ویژه برای تیم ===
      if (user?.is_teammate === 1) {
        let products_number = orderState.products_info.length;
        let total_weight = 0;
        let total_kaser = 0;
        let order_products_ojrat = 0;

        orderState.products_info.forEach((product) => {
          // اگر فرمول وجود داشته باشد از آن استفاده کنیم
          if (product.formula) {
            total_kaser += (parseFloatNull(product.formula.k) * parseFloatNull(product.weight)) / 100;
            order_products_ojrat += parseFloatNull(product.formula.o) * parseFloatNull(product.weight);
          }
          total_weight += parseFloatNull(product.weight);
        });

        orderState.order_shipping_ojrat = order_products_ojrat + parseFloatNull(orderState.shipping_cost);
        orderState.order_products_ojrat = parseFloat(order_products_ojrat).toFixed(2);
        orderState.total_gr = parseFloat(total_weight + total_kaser).toFixed(2);
        orderState.total_weight = parseFloat(total_weight).toFixed(2);
        orderState.total_kaser = parseFloat(total_kaser).toFixed(2);
        orderState.products_number = products_number;
      } else {
        // === محاسبات برای مشتری عادی ===
        orderState.final_price = totalProductPrice;
      }

      return orderState;
    },
    [server_url, user, dates, image_url],
  );

  const getOrders = useCallback(
    async (page = 1, status = null) => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          all: 1,
          page: page, // اضافه کردن page به params
        };

        // اگر استاتوس ارسال شده باشد، آن را به params اضافه کن
        if (status) {
          params.status = status;
        }

        const response = await axios({
          url: server_url + 'orders',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          method: 'GET',
          params,
        });

        const responseData = response.data;

        if (responseData.status) {
          const ordersData = responseData.data.orders;
          const processedOrders = ordersData.map((order, index) => processOrderData(order, index));
          setDefaultOrders(responseData.data.orders);

          setOrders(processedOrders);
          setCurrentStatus(status); // ذخیره فیلتر فعلی

          // به‌روزرسانی pagination
          setPagination({
            current_page: responseData.current_page,
            last_page: responseData.last_page,
            per_page: responseData.per_page,
            total: responseData.total,
          });

          // به‌روزرسانی آمار وضعیت‌ها
          setStatusCounts(responseData.data.status_counts || {});
        } else {
          throw new Error('خطا در دریافت داده‌ها');
        }
      } catch (err) {
        setError(err.message || 'خطا در دریافت سفارش‌ها');
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    },
    [server_url, processOrderData],
  );

  // تابع refresh برای بارگذاری مجدد
  const refreshOrders = useCallback(
    (page = 1, status = null) => {
      getOrders(page, status);
    },
    [getOrders],
  );

  // پاک کردن خطا
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // فیلتر سفارش‌ها بر اساس وضعیت
  const getOrdersByStatus = useCallback(
    (status) => {
      return orders.filter((order) => order.status === status);
    },
    [orders],
  );

  // آمار سفارش‌ها از API
  const ordersStats = useCallback(() => {
    return {
      delivered: statusCounts.delivered || 0,
      failed: statusCounts.failed || 0,
      packaged: statusCounts.packaged || 0,
      packaging: statusCounts.packaging || 0,
      paid: statusCounts.paid || 0,
      total: pagination.total || 0,
    };
  }, [statusCounts, pagination.total]);

  // تابع برای دریافت صفحه خاص
  const loadPage = useCallback(
    (page, status = null) => {
      getOrders(page, status);
    },
    [getOrders],
  );

  // تابع برای فیلتر کردن سفارش‌ها بر اساس استاتوس از سرور
  const getOrdersByStatusFromServer = useCallback(
    async (status, page = 1) => {
      await getOrders(page, status);
    },
    [getOrders],
  );

  // تابع برای دریافت تمام سفارش‌ها (حذف فیلتر)
  const getAllOrders = useCallback(
    async (page = 1) => {
      await getOrders(page, null);
    },
    [getOrders],
  );

  // تابع برای رفتن به صفحه بعدی با حفظ فیلتر فعلی
  const goToNextPage = useCallback(async () => {
    if (pagination.current_page < pagination.last_page) {
      await getOrders(pagination.current_page + 1, currentStatus);
    }
  }, [getOrders, pagination.current_page, pagination.last_page, currentStatus]);

  // تابع برای رفتن به صفحه قبلی با حفظ فیلتر فعلی
  const goToPrevPage = useCallback(async () => {
    if (pagination.current_page > 1) {
      await getOrders(pagination.current_page - 1, currentStatus);
    }
  }, [getOrders, pagination.current_page, currentStatus]);

  // تابع برای رفتن به صفحه خاص با حفظ فیلتر فعلی
  const goToPage = useCallback(
    async (page) => {
      if (page >= 1 && page <= pagination.last_page) {
        await getOrders(page, currentStatus);
      }
    },
    [getOrders, pagination.last_page, currentStatus],
  );

  return {
    defaultOrders,
    orders,
    loading,
    error,
    pagination,
    statusCounts,
    currentStatus,
    total: pagination.total, // اضافه کردن total برای دسترسی راحت‌تر
    lastPage: pagination.last_page, // اضافه کردن last_page برای دسترسی راحت‌تر
    getOrders,
    refreshOrders,
    clearError,
    getOrdersByStatus,
    getOrdersByStatusFromServer,
    getAllOrders,
    goToNextPage,
    goToPrevPage,
    goToPage,
    ordersStats,
    loadPage,
  };
};

export default useOrders;
